"use client";

import { useRouter } from "next/navigation";
import React, { Fragment, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Divider } from "staysure-component-library";

import { ApiError } from "@/app/common-types/api-error.types";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import SingleACOSection from "@/components/molecules/quote-section/acos/single-aco-section";
import withApplication from "@/components/with-application";
import { RenewalPage } from "@/config/app-constant";
import useUpdateAcoData from "@/hooks/api/quote/use-update-aco";
import { RootState } from "@/store/root-reducer";
import { setApiError } from "@/store/slices/errors/api-error.slice";
import {
  updateQuote,
  setCoverLevelChangedState,
} from "@/store/slices/quote/quote.slice";
import { OptionalExtra } from "@/types/OptionalExtraType";
import { apiErrorMappings } from "@/utils/errors/api-error-map";
import handleError, { ErrorResponse } from "@/utils/handle-errors";
import { AcoSectionContentType } from "@/utils/schema/quote-page-schema.utils";
import { checkCurentLocation } from "@/utils/travel-date.utils";

import getAcoData from "./dynamic-data-map/map-aco-data";

function getFullPageRelativeTop(element: HTMLElement | null): number {
  let top = 0;
  let elementAssign = element;

  while (elementAssign) {
    top += elementAssign.offsetTop;

    elementAssign = elementAssign.offsetParent as HTMLElement | null;
  }

  return top;
}

function AcoSection({
  acoSectionContent,
  application,
}: Readonly<{
  acoSectionContent: AcoSectionContentType | null;
  application?: string;
}>) {
  const dispatch = useDispatch();
  const router = useRouter();
  const acoRef = useRef<HTMLDivElement>(null);

  const [acoObj, setAcoObj] = React.useState<OptionalExtra[] | undefined>([]);
  const { attributes, isCoverLevelChanged } = useSelector(
    (state: RootState) => state.quote
  );
  const {
    tripDetails,
    priceResult,
    paymentDetails,
    travellerDetails,
    acoSection,
  } = attributes;
  const [coverLevel, setCoverLevel] = React.useState("");
  const [isPclEligible, setIsPclEligible] = React.useState(false);
  const [type, setType] = React.useState("");

  const { current } = useWizard();

  useEffect(() => {
    if (isCoverLevelChanged) {
      setTimeout(function () {
        const topOffset = getFullPageRelativeTop(
          acoRef?.current as HTMLElement
        );
        window.scrollTo({ top: topOffset, behavior: "smooth" });
      }, 200);
    }
    dispatch(setCoverLevelChangedState(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCoverLevelChanged]);

  useEffect(() => {
    if (tripDetails) {
      if (tripDetails.policyType) setType(tripDetails.policyType);

      if (tripDetails.policyPackage) {
        setCoverLevel(tripDetails.policyPackage);
      }
    }
  }, [tripDetails]);

  useEffect(() => {
    if (priceResult) {
      setAcoObj(priceResult?.optionalExtras);
    }
  }, [priceResult]);

  useEffect(() => {
    if (paymentDetails) {
      setIsPclEligible(
        paymentDetails.paymentOptions[0].cardPayment.premiumCreditPayment[0].pcl
          .eligible
      );
    }
  }, [paymentDetails]);

  const { data, loading, error, updateAcoData } = useUpdateAcoData();

  const onChanggeAcos = (isSelect: boolean, index: number) => {
    if (acoObj !== undefined) {
      const acoArr = [...acoObj];
      acoArr[index] = {
        ...acoArr[index],
        price: [
          {
            ...acoArr[index].price[0],
            policyType: type,
            policyPackage: coverLevel,
            selected: isSelect,
          },
        ],
      };
      setAcoObj(acoArr);
      updateAcoData(acoArr);
    }
  };

  const onChanggeGolfAco = (value: string, index: number) => {
    if (acoObj !== undefined) {
      const acoArr = [...acoObj];
      acoArr[index] = {
        ...acoArr[index],
        price: [
          {
            ...acoArr[index].price[0],
            policyType: type,
            policyPackage: coverLevel,
            selected: value !== "0",
            detail: {
              value,
              description: null,
            },
          },
        ],
      };
      setAcoObj(acoArr);
      updateAcoData(acoArr);
    }
  };

  const onChanggeSingleItemAco = (value: string, index: number) => {
    if (acoObj !== undefined) {
      const acoArr = [...acoObj];
      acoArr[index] = {
        ...acoArr[index],
        price: [
          {
            ...acoArr[index].price[0],
            policyType: type,
            policyPackage: coverLevel,
            selected: value !== "0",
            detail: {
              value,
              description: null,
            },
          },
        ],
      };

      setAcoObj(acoArr);
      updateAcoData(acoArr);
    }
  };

  useEffect(() => {
    const handleEffect = async () => {
      if (!loading && data && !error) {
        dispatch(
          updateQuote({
            ...data,
            attributes: { ...data.attributes, acoSection },
          })
        );
      } else if (!loading && error !== null) {
        if (application === RenewalPage) {
          await handleError(error as ApiError<ErrorResponse>, router, dispatch);
        } else if (error.response?.status === 401) {
          router.push("/");
        } else {
          const errorObject = await apiErrorMappings(error);
          errorObject.multiButton = true;
          dispatch(setApiError(errorObject));
        }
      }
    };
    handleEffect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading, error, dispatch, router]);

  const sameCountryCheck = checkCurentLocation(
    attributes.tripDetails?.fromLocation ?? "",
    attributes.tripDetails?.toLocation ?? ""
  );

  const getRelatedAcoApiData = (
    acoCode: string,
    isExcludeSameCountry: boolean
  ) => {
    if (
      attributes.tripDetails?.policyType === "SINGLE_TRIP" &&
      sameCountryCheck
    ) {
      return priceResult?.optionalExtras.filter(
        (value: OptionalExtra) =>
          value.code === acoCode &&
          !isExcludeSameCountry &&
          value.code !== "PERSONAL_LIABILITY_COVER"
      );
    }
    return priceResult?.optionalExtras.filter(
      (value: OptionalExtra) => value.code === acoCode
    );
  };

  const enabledAcoItems = getAcoData(
    acoSectionContent?.acoItem ?? undefined
  ).filter((acoData) => {
    const relatedAcoApiData = getRelatedAcoApiData(
      acoData.code,
      acoData.data.isExcludeSameCountryTravel
    );

    return (
      relatedAcoApiData &&
      relatedAcoApiData.length > 0 &&
      relatedAcoApiData[0]?.price[0].state === "ENABLED"
    );
  });

  if (
    enabledAcoItems.length === 0 &&
    current?.brandSpecificParams?.isDisableExtraCoverOptions
  ) {
    return null;
  }

  if (
    priceResult?.optionalExtras.filter(
      (value: OptionalExtra) => value.price[0].state === "ENABLED"
    ).length === 0
  ) {
    return null;
  }
  if (!acoSectionContent) {
    return null;
  }

  const {
    additionalCoverOptionsSectionTitle,
    removeBtnLabel,
    moreInfoBtnLabel,
    addBtnLabel,
  } = acoSectionContent;

  const { addLabel, addedLabel } = addBtnLabel;
  return (
    <div data-testid="AcoSection-test-id" ref={acoRef}>
      <div
        className="md:max-w-[1199px]  min-w-[312px] mx-spacing-l md:mx-spacing-xxl lg:mx-auto my-0 md:w-auto relative bg-surface-neutral-option-1 
  md:p-spacing-xxl p-spacing-m rounded-radius-xxl"
      >
        <div className="mb-spacing-l font-header-font text-[26px] md:text-[39px] leading-[115%] font-[400] text-text-title">
          {additionalCoverOptionsSectionTitle}
        </div>
        <div
          className={` grid lg:grid grid-cols-1 lg:grid-cols-2 gap-y-spacing-0 md:gap-y-spacing-l lg:gap-x-spacing-5xl`}
        >
          {getAcoData(acoSectionContent?.acoItem ?? undefined).map(
            (
              acoData: {
                code: string;
                data: {
                  acoTitle: string;
                  description: string;
                  drawerContent: string;
                  drawerCloseCta: string;
                  removeModalTitle: string;
                  removeModalDescription: string;
                  removeModalConfirmCTALabel: string;
                  removeModalCancelCTALabel: string;
                  isExcludeSameCountryTravel: boolean;
                  tdeModalTitle?: string;
                  tdeModalCopy?: string;
                  tdeModalConfirmBtnLabel?: string;
                  tdeModalCancelBtnLabel?: string;
                  isTravelDisruption?: boolean;
                  isGolfCover?: boolean;
                  isSingleItemCover?: boolean;
                };
              },
              index: number
            ) => {
              const aco = {
                ...acoData,
                data: {
                  ...acoData.data,
                  tdeModalTitle: acoSectionContent.tdeModalTitle,
                  tdeModalCopy: acoSectionContent.tdeModalCopy,
                  tdeModalConfirmBtnLabel:
                    acoSectionContent.tdeModalConfirmBtnLabel,
                  tdeModalCancelBtnLabel:
                    acoSectionContent.tdeModalCancelBtnLabel,
                  isTravelDisruption: acoData.data.isTravelDisruption,
                },
              };

              const relatedAcoApiData = getRelatedAcoApiData(
                aco.code,
                aco.data.isExcludeSameCountryTravel
              );

              const relatedAcoApiDataIndex =
                priceResult?.optionalExtras.findIndex(
                  (value: OptionalExtra) => value.code === aco.code
                );

              const isSelected = () => {
                if (relatedAcoApiData && relatedAcoApiDataIndex !== undefined) {
                  if (aco.data.isGolfCover || aco.data.isSingleItemCover) {
                    return relatedAcoApiData[0]?.price[0]?.selected
                      ? relatedAcoApiData[0]?.price[0]?.detail?.value
                      : "0";
                  }
                  return relatedAcoApiData[0]?.price[0]?.selected;
                }
                return false;
              };

              return (
                <Fragment key={aco.code}>
                  {relatedAcoApiData &&
                    relatedAcoApiDataIndex !== undefined &&
                    relatedAcoApiData[0]?.price[0].state === "ENABLED" && (
                      <div>
                        <SingleACOSection
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...aco.data}
                          type={type}
                          priceYear={
                            relatedAcoApiData[0]?.price[0]?.sellPrice ?? 0
                          }
                          priceMonth={
                            isPclEligible
                              ? relatedAcoApiData[0]?.price[0]?.pclPrice ?? null
                              : null
                          }
                          totalPCLCost={
                            isPclEligible
                              ? relatedAcoApiData[0]?.price[0]?.pclTotalCost ??
                                null
                              : null
                          }
                          isSelected={isSelected()}
                          onSelect={(e: string | boolean) => {
                            if (aco.data.isGolfCover === true) {
                              onChanggeGolfAco(
                                e as string,
                                relatedAcoApiDataIndex
                              );
                            } else if (aco.data.isSingleItemCover === true) {
                              onChanggeSingleItemAco(
                                e as string,
                                relatedAcoApiDataIndex
                              );
                            } else {
                              onChanggeAcos(
                                e as boolean,
                                relatedAcoApiDataIndex
                              );
                            }
                          }}
                          addBtnLabel={addLabel}
                          addedBtnLabel={addedLabel}
                          removeBtnLabel={removeBtnLabel}
                          moreInfoBtnLabel={moreInfoBtnLabel}
                          travelers={travellerDetails?.traveller}
                          id={index}
                          code={aco.code}
                          isTravelDisruption={aco.data.isTravelDisruption}
                        />
                        <Divider
                          className="md:hidden !my-spacing-m"
                          color="#DCDCDC"
                        />
                      </div>
                    )}
                </Fragment>
              );
            }
          )}
        </div>
      </div>
    </div>
  );
}

export default withApplication(AcoSection);
